<template>
    <el-container>
        <el-header>
            <span>{{$t('account.title')}}</span>
        </el-header>
        <el-main class="account-main">
            <div v-if="loading && !accountLoaded" class="account-initial-loading" v-loading="true"></div>
            <el-alert v-else-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
            <div v-else-if="accountLoaded" class="account-grid">
                <el-card class="account-card account-profile-card">
                    <div class="account-card-title">{{$t('account.profile')}}</div>
                    <div class="account-email">{{loginInformation.email}}</div>
                    <div class="account-meta">{{$t('account.swiftAccount')}}</div>

                    <div class="connect-section">
                        <div class="connect-title">{{$t('account.connectSocial')}}</div>
                        <div class="connect-row">
                            <button type="button"
                                    class="connect-button connect-google"
                                    :class="{'is-bound': googleBound !== null}"
                                    :disabled="googleBinding || googleBound !== null"
                                    @click.prevent="handleConnectGoogle">
                                <span class="connect-logo" aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 48 48">
                                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                                    </svg>
                                </span>
                                <span class="connect-label">{{ googleLabel }}</span>
                            </button>
                            <button type="button"
                                    class="connect-button connect-apple"
                                    :class="{'is-bound': appleBound !== null}"
                                    :disabled="appleBinding || appleBound !== null"
                                    @click.prevent="handleConnectApple">
                                <img src="@/static/icon/apple.svg" alt="" class="connect-logo connect-logo-img">
                                <span class="connect-label">{{ appleLabel }}</span>
                            </button>
                        </div>
                    </div>
                </el-card>

                <el-card class="account-card account-logo-card">
                    <div class="account-card-title">{{$t('account.companyLogo')}}</div>
                    <div v-if="haveLogo==1" class="logo-row">
                        <div class="logo-preview">
                            <el-image :src="fileInfo.url">
                                <div slot="placeholder" class="remote-image-loading">
                                    <i class="el-icon-loading"></i>
                                </div>
                                <div slot="error" class="logo-image-error">
                                    <i class="el-icon-warning-outline"></i>
                                    <span>{{$t('account.logoUnavailable')}}</span>
                                </div>
                            </el-image>
                        </div>
                        <div class="logo-actions">
                            <el-upload
                                    :action=fullRequestURL
                                    :show-file-list="false"
                                    :on-success="handleSuccess"
                                    :on-error="handleUploadError"
                                    :before-upload="onBeforeUpload"
                                    :data="uploadData"
                                    accept="image/jpeg,image/png,image/bmp">
                                <el-button type="primary" plain :loading="attachPhotosStatus">{{$t('common.change')}}</el-button>
                            </el-upload>
                            <el-button class="logo-delete-button" @click="handleRemove()"
                                       :loading="deleteLogoStatus" type="text">{{$t('common.delete')}}
                            </el-button>
                        </div>
                    </div>

                    <div v-else class="logo-empty-row">
                        <el-upload
                                :action=fullRequestURL
                                class="avatar-uploader"
                                :show-file-list="false"
                                :on-success="handleSuccess"
                                :on-error="handleUploadError"
                                :before-upload="onBeforeUpload"
                                :data="uploadData"
                                accept="image/jpeg,image/png,image/bmp">
                            <div class="logo-upload-box">
                                <i class="el-icon-plus avatar-uploader-icon"></i>
                                <span>{{addLogoText || $t('account.addLogo')}}</span>
                            </div>
                        </el-upload>
                    </div>
                </el-card>

                <el-card class="account-card account-plan-card">
                    <div class="plan-summary-line">
                        <span class="current-plan-name">{{$t('account.currentPlan', {plan: currentPlan})}}</span>
                        <span v-if="currentPlan !== 'Free' || isSubscriptionExpired" class="plan-expiry">{{$t('account.planActiveUntil', {date: formatEnglishDate1(loginInformation.expiresTime* 1000)})}}</span>
                        <span v-if="currentPlan !== 'Free'" class="plan-share-note">{{$t('account.sharedWithMobile')}}</span>
                    </div>
                    <div v-if="isSubscriptionExpired" class="plan-expired-note">{{$t('account.subscriptionExpired')}}</div>
                    <div class="plan-note">{{$t('account.planNote')}}</div>
                    <el-button class="subscriptionButton" type="success" @click.native.prevent="openSubscriptionPage">
                        {{subscriptionButtonText}}
                    </el-button>
                </el-card>

            </div>
        </el-main>
    </el-container>
</template>

<script>
    import {timest} from '@/utils/validate'
    import {formatEnglishDate} from '@/utils/time'
    import {getCurrentPlanName, isSubscriptionExpired as checkSubscriptionExpired, openFastSpringPage} from '@/utils/subscription'
    import {
        COMPANY_LOGO_FOLDER,
        discardPreparedFileUpload,
        getDownloadV2Url,
        getFileUploadUrl,
        getStoredImageDownloadUrl,
        getUploadRemoteKey,
        prepareFileUpload,
        takeUploadInnerUUID
    } from '@/utils/fileUpload'
    import {
        PROVIDER_GOOGLE,
        PROVIDER_APPLE,
        signInWithGoogle,
        initGoogleSignIn,
        signInWithApple,
        bindLoggedInSocialLogin
    } from '@/utils/socialLogin'
    import {replaceStoredCompany, syncCompanySelection} from '@/utils/company'
    import {reportHandledError} from '@/utils/sentry'


    export default {
        methods: {
            formatEnglishDate1: formatEnglishDate,

            openSubscriptionPage() {
                if (!this.accountLoaded) {
                    return;
                }
                openFastSpringPage(this.currentPlan, this.currentPlan === 'Premium' ? 'Premium' : 'Pro');
            },
            async queryUserInformation() {
                this.loading = true;
                this.accountLoaded = false;
                this.loadError = '';
                try {
                    const responses = await Promise.all([
                        this.$axios.post('get_auth', {params: {}}),
                        this.$axios.post('query_setting_and_company', {params: {}})
                    ]);
                    const authResponse = responses[0];
                    const companyResponse = responses[1];
                    if (authResponse.data.errorCode !== 0 || authResponse.data.objectData == null) {
                        throw new Error('Account query failed');
                    }
                    if (companyResponse.data.errorCode !== 0) {
                        throw new Error('Company query failed');
                    }
                    const selectedData = syncCompanySelection(companyResponse.data);
                    if (selectedData == null) {
                        throw new Error('Selected company data is missing');
                    }
                    this.userCompany = selectedData.company;
                    this.loginInformation = authResponse.data.objectData;
                    this.uploadData.email = this.loginInformation.email;
                    this.googleBound = this.loginInformation.googleBound != null ? this.loginInformation.googleBound : null;
                    this.appleBound = this.loginInformation.appleBound != null ? this.loginInformation.appleBound : null;

                    const companyLogo = authResponse.data.companyLogo;
                    if (companyLogo != null) {
                        this.fileInfo = {
                            url: getStoredImageDownloadUrl(this.$store.state.requestURL, companyLogo),
                            innerUUID: companyLogo.innerUUID
                        };
                        this.haveLogo = 1;
                        localStorage.setItem('logoPath', companyLogo.imagePath);
                    } else {
                        this.haveLogo = 0;
                        this.fileInfo = {};
                        localStorage.setItem('logoPath', "");
                    }

                    const storedLoginInformation = JSON.parse(localStorage.getItem("loginInformation"));
                    if (storedLoginInformation == null || storedLoginInformation.objectData == null) {
                        throw new Error('Stored login information is missing');
                    }
                    storedLoginInformation.objectData.expiresTime = this.loginInformation.expiresTime;
                    storedLoginInformation.objectData.iapStatus = this.loginInformation.iapStatus;
                    storedLoginInformation.objectData.planType = this.loginInformation.planType;
                    localStorage.setItem('loginInformation', JSON.stringify(storedLoginInformation));
                    this.accountLoaded = true;
                } catch {
                    this.loadError = this.$t('common.loadFailed');
                } finally {
                    this.loading = false;
                }
            },
            ///////////////上传///////////////
            handleRemove() {
                if (!this.accountLoaded || this.deleteLogoStatus) {
                    return;
                }
                let that = this;
                this.deleteLogoStatus = true;
                let companyLogoUUID = that.fileInfo.innerUUID;
                if (companyLogoUUID == null || companyLogoUUID === "") {
                    that.deleteLogoStatus = false;
                    this.$message({
                        type: 'error',
                        message: this.$t('account.logoUuidMissing'),
                        duration: 6000
                    });
                    return;
                }
                this.$axios.post('update_company', {
                    params: {
                        companyLogoUUIDArrayKey: "",
                        innerUUID: that.userCompany.innerUUID,
                        companyUUID: that.userCompany.innerUUID,
                        accessDate: timest()
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.$axios.post('update_company_logo', {
                            params: {
                                innerUUID: companyLogoUUID,
                                companyUUID: that.userCompany.innerUUID,
                                syncStatus: 1
                            }
                        }).then(response => {
                            if (response.data.errorCode === 0) {
                                this.$message({
                                    type: 'info',
                                    message: this.$t('common.deleteSuccess'),
                                    duration: 6000
                                });
                                that.userCompany.companyLogoUUIDArrayKey = "";
                                replaceStoredCompany(that.userCompany);
                                localStorage.setItem('logoPath', "");
                                that.haveLogo = 0;
                                that.deleteLogoStatus = false;
                                this.fileInfo = {}
                            } else {
                                that.deleteLogoStatus = false;
                                this.$message({
                                    type: 'error',
                                    message: this.$t('common.deleteFailed'),
                                    duration: 6000
                                });
                            }
                        }).catch(() => {
                            that.deleteLogoStatus = false;
                            this.$message({
                                type: 'error',
                                message: this.$t('common.deleteFailed'),
                                duration: 6000
                            });
                        })

                    } else {
                        that.deleteLogoStatus = false;
                        this.$message({
                            type: 'error',
                            message: this.$t('common.deleteFailed'),
                            duration: 6000
                        });
                    }
                }).catch(() => {
                    that.deleteLogoStatus = false;
                    this.$message({
                        type: 'error',
                        message: this.$t('common.deleteFailed'),
                        duration: 6000
                    });
                })
            },
            onBeforeUpload(file) {
                if (!this.accountLoaded || this.attachPhotosStatus) {
                    return false;
                }
                prepareFileUpload(this.uploadData, this.uploadFileMap, file, COMPANY_LOGO_FOLDER);
                this.uploadData.createDate = timest();
                if (this.haveLogo == 1) {
                    this.uploadData.oldCompanyLogoUUID = this.fileInfo.innerUUID;
                } else {
                    delete this.uploadData.oldCompanyLogoUUID;
                }
                this.addLogoText = this.$t('account.uploading');
                this.attachPhotosStatus = true;
            },
            showLogoUploadError(message) {
                this.addLogoText = "";
                this.attachPhotosStatus = false;
                this.$message({
                    type: 'error',
                    message: message,
                    duration: 6000
                });
            },
            reportLogoUploadError(error, file, operation) {
                reportHandledError(error, {
                    level: 'error',
                    module: 'file_upload',
                    operation: operation,
                    tags: {
                        file_type: 'company_logo'
                    },
                    context: {
                        fileName: file == null ? '' : file.name,
                        fileSize: file == null ? 0 : file.size,
                        folderName: COMPANY_LOGO_FOLDER
                    }
                });
            },
            handleUploadError(error, file) {
                discardPreparedFileUpload(this.uploadFileMap, file);
                this.reportLogoUploadError(error, file, 'company_logo_upload_request');
                this.showLogoUploadError(this.$t('account.logoUploadFailed'));
            },
            handlePictureCardPreview(file) {
                this.dialogImageUrl = file.url;
                this.dialogVisible = true;
            },
            handleSuccess(res, file) {
                this.addLogoText = "";
                this.attachPhotosStatus = false;
                if (res == null || Number(res.errorCode) !== 0 || !Array.isArray(res.uploadFileList) || res.uploadFileList.length !== 1) {
                    discardPreparedFileUpload(this.uploadFileMap, file);
                    this.reportLogoUploadError(new Error(res == null || res.errorMsg == null ? 'company logo upload failed' : res.errorMsg), file, 'company_logo_upload_response');
                    this.showLogoUploadError(this.$t('account.logoUploadFailed'));
                    return;
                }
                const innerUUID = takeUploadInnerUUID(this.uploadFileMap, file);
                const remoteKey = getUploadRemoteKey(COMPANY_LOGO_FOLDER, innerUUID);
                this.haveLogo = 1;
                this.fileInfo = {
                    name: innerUUID,
                    url: getDownloadV2Url(this.$store.state.requestURL, remoteKey),
                    innerUUID: innerUUID,
                    remoteKey: remoteKey
                };
                this.userCompany.companyLogoUUIDArrayKey = [innerUUID];
                replaceStoredCompany(this.userCompany);
                localStorage.setItem('logoPath', res.uploadFileList[0].url);
                this.$message({
                    type: 'info',
                    message: this.$t('common.uploadSuccess'),
                    duration: 6000
                });
            },
            handleConnectGoogle() {
                this.handleConnectSocial(PROVIDER_GOOGLE, signInWithGoogle, 'googleBinding', 'googleBound');
            },
            handleConnectApple() {
                this.handleConnectSocial(PROVIDER_APPLE, signInWithApple, 'appleBinding', 'appleBound');
            },
            handleConnectSocial(provider, signInFn, bindingKey, boundKey) {
                let self = this;
                if (!this.accountLoaded || this[bindingKey] || this[boundKey] !== null) {
                    return;
                }
                this[bindingKey] = true;
                signInFn().then(function (result) {
                    return bindLoggedInSocialLogin(self.$axios, {
                        provider: provider,
                        idToken: result.idToken,
                        rawNonce: result.rawNonce
                    });
                }).then(function (response) {
                    self[bindingKey] = false;
                    if (response.data.errorCode === 0) {
                        // Bound; prefer the social email returned by the backend, else "" (bound, email unknown).
                        const obj = response.data.objectData || {};
                        const email = provider === PROVIDER_GOOGLE ? obj.googleBound : obj.appleBound;
                        self[boundKey] = email != null ? email : "";
                        self.$message({
                            type: 'success',
                            message: provider === PROVIDER_GOOGLE ? self.$t('account.googleConnectedToast') : self.$t('account.appleConnectedToast'),
                            duration: 2000
                        });
                    } else if (response.data.errorCode === 225) {
                        // This provider is already bound to the current account.
                        self[boundKey] = "";
                        self.$message.error(self.$t('common.saveFailed'));
                    } else {
                        self.$message.error(self.$t('common.saveFailed'));
                    }
                }).catch(function () {
                    self[bindingKey] = false;
                    self.$message.error(self.$t('common.systemError'));
                });
            }
        },
        computed: {
            currentPlan() {
                return getCurrentPlanName(this.loginInformation);
            },
            isSubscriptionExpired() {
                return checkSubscriptionExpired(this.loginInformation);
            },
            subscriptionButtonText() {
                return this.currentPlan === "Free" ? this.$t('account.upgradeToPro') : this.$t('account.viewPlans');
            },
            googleLabel() {
                if (this.googleBinding) {
                    return this.$t('account.connecting');
                }
                if (this.googleBound === null) {
                    return this.$t('account.connectGoogle');
                }
                return this.googleBound ? this.$t('account.googleConnectedWithEmail', {email: this.googleBound}) : this.$t('account.googleConnected');
            },
            appleLabel() {
                if (this.appleBinding) {
                    return this.$t('account.connecting');
                }
                if (this.appleBound === null) {
                    return this.$t('account.connectApple');
                }
                return this.appleBound ? this.$t('account.appleConnectedWithEmail', {email: this.appleBound}) : this.$t('account.appleConnected');
            }
        },
        data() {
            return {
                deleteLogoStatus: false,
                loginInformation: {},
                userCompany: {},
                loading: true,
                accountLoaded: false,
                loadError: '',
                requestURL: "",
                attachPhotosStatus: false,
                uploadData: {
                    email: "",
                    needUpdateCompanyLogo: 1,
                    createDate: timest()
                },
                uploadFileMap: {},
                fileInfo: {},
                fullRequestURL: "",
                haveLogo: 0,
                addLogoText: "",
                loadImgPic: "@/static/loading.svg",
                googleBinding: false,
                googleBound: null,
                appleBinding: false,
                appleBound: null
            }
        },
        mounted() {
            this.queryUserInformation();

            this.fullRequestURL = getFileUploadUrl(this.$store.state.requestURL);

            // Pre-render the hidden Google Sign-In button for the Connect section.
            initGoogleSignIn();
        }
    }
</script>
<style scoped>
    .account-initial-loading {
        min-height: 240px;
    }

    /deep/ .el-header {
        background-color: #f7f7f9;
        font-size: 25px;
        font-weight: 700;
        height: 100px !important;
        display: flex;
        justify-content: space-between;
        padding: 30px;
    }

    .account-main {
        background: #F7F8FA;
    }

    .account-grid {
        display: grid;
        grid-template-columns: minmax(260px, 1fr) minmax(320px, 1.4fr);
        gap: 20px;
        max-width: 1120px;
    }

    .account-card {
        border: 1px solid #E6E8EF;
        border-radius: 8px;
        box-shadow: 0 6px 18px rgba(31, 42, 68, 0.07);
    }

    .logo-image-error {
        align-items: center;
        color: #C45656;
        display: flex;
        flex-direction: column;
        font-size: 13px;
        gap: 6px;
        justify-content: center;
        min-height: 104px;
        padding: 8px;
        text-align: center;
    }

    .logo-image-error i {
        font-size: 22px;
    }

    .account-card-title {
        margin-bottom: 18px;
        color: #303133;
        font-size: 18px;
        font-weight: 700;
    }

    .account-profile-card {
        min-height: 170px;
    }

    .account-email {
        color: #1F2D3D;
        font-size: 20px;
        font-weight: 700;
        word-break: break-word;
    }

    .account-meta,
    .plan-note {
        margin-top: 8px;
        color: #7A8494;
        font-size: var(--responsive-font-14);
        line-height: 20px;
    }

    .connect-section {
        margin-top: 22px;
        padding-top: 18px;
        border-top: 1px solid #E6E8EF;
    }

    .connect-title {
        margin-bottom: 12px;
        color: #303133;
        font-size: 15px;
        font-weight: 700;
    }

    .connect-row {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .connect-button {
        width: 100%;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        border: 1px solid #d7dee9;
        border-radius: 6px;
        background: #ffffff;
        color: #17213c;
        font-size: var(--responsive-font-14);
        font-weight: 600;
        cursor: pointer;
        transition: background 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
    }

    .connect-button:hover:not(:disabled) {
        background: #f6f9ff;
        border-color: #b9c6db;
        box-shadow: 0 6px 14px rgba(31, 45, 74, 0.06);
    }

    .connect-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .connect-google.is-bound,
    .connect-apple.is-bound {
        background: #f0f9eb;
        border-color: #c2e7b0;
        color: #67c23a;
    }

    .connect-logo {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        flex: 0 0 16px;
    }

    .connect-logo-img {
        width: 16px;
        height: 16px;
    }

    .account-logo-card {
        min-height: 170px;
    }

    .logo-row {
        display: flex;
        align-items: center;
        gap: 20px;
    }

    .logo-preview {
        width: 104px;
        height: 104px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border: 1px solid #E6E8EF;
        border-radius: 8px;
        background: #FFFFFF;
    }

    .logo-preview >>> .el-image {
        width: 100%;
        height: 100%;
    }

    .logo-preview >>> img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }

    .logo-actions {
        display: flex;
        align-items: center;
        gap: 14px;
    }

    .logo-delete-button {
        color: #F56C6C;
    }

    .logo-empty-row {
        display: flex;
        align-items: center;
    }

    .logo-upload-box {
        width: 160px;
        height: 104px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #409EFF;
        border: 1px dashed #B8C4D6;
        border-radius: 8px;
        background: #F8FBFF;
    }

    .account-plan-card {
        grid-column: 1 / -1;
    }

    .plan-summary-line {
        display: flex;
        align-items: baseline;
        gap: 18px;
        flex-wrap: wrap;
    }

    .current-plan-name {
        color: #1F2D3D;
        font-size: 24px;
        font-weight: 700;
    }

    .plan-expiry {
        color: #303133;
        font-size: 18px;
        font-weight: 600;
    }

    .plan-share-note {
        color: #409EFF;
        font-size: 15px;
        font-weight: 600;
        white-space: nowrap;
    }

    .plan-expired-note {
        margin-top: 8px;
        color: #F56C6C;
        font-size: 15px;
        font-weight: 600;
    }

    .subscriptionButton {
        margin-top: 16px;
        background-color: #0D7DF7;
        border-color: #0D7DF7;
        font-weight: 600;
    }

    .subscriptionButton:focus,
    .subscriptionButton:hover {
        background-color: #0B6FE0;
        border-color: #0B6FE0;
    }

    @media (max-width: 760px) {
        .account-grid {
            grid-template-columns: 1fr;
        }

        /deep/ .el-dialog {
            width: calc(100% - 32px) !important;
        }
    }

</style>

<style scoped>
    .el-button--success {
        background-color: #0D7DF7;
        border-color: #0D7DF7;
    }

    .el-button--success:focus, .el-button--success:hover {
        background-color: #0D7DF7;
        border-color: #0D7DF7;
    }

    /deep/ .avatar-uploader .el-upload {
        cursor: pointer;
        position: relative;
        overflow: hidden;
    }

    /deep/ .avatar-uploader .el-upload:hover {
        border-color: #409EFF;
    }

    /deep/ .avatar-uploader-icon {
        font-size: 28px;
        color: #8c939d;
        width: auto;
        height: auto;
        line-height: 1;
        text-align: center;
    }

    /deep/ .avatar {
        width: 50px;
        height: 50px;
        display: block;
    }
</style>
